import axiosInstance from '../../../lib/axios';
import { PaginatedInboxMessages, ChatConversation } from '../domain/types';

export const InboxService = {
  getConversations: async (search?: string, limit: number = 50): Promise<ChatConversation[]> => {
    const response = await axiosInstance.get('/inbox/conversations', { params: { search, limit } });
    const raw: any[] = response.data.data || [];

    // The API groups by phone number and returns _id = phone_number.
    // Map _id → phone_number so the rest of the app can use phone_number consistently.
    return raw.map((item) => ({
      ...item,
      phone_number: item.phone_number ?? item._id,
    }));
  },

  getConversationMessages: async (
    phoneNumber: string, 
    page: number = 1, 
    page_size: number = 50
  ): Promise<PaginatedInboxMessages> => {
    const encodedPhone = encodeURIComponent(phoneNumber);
    const response = await axiosInstance.get(`/inbox/conversations/${encodedPhone}/messages`, {
      params: { page, page_size }
    });
    
    // Shape: { success, data: [...messages], pagination: { total, page, page_size, has_more } }
    const messages = (response.data.data || []).map((msg: any) => ({
      ...msg,
      // Normalise _id alias: Pydantic by_alias=True serialises id → _id
      _id: msg._id ?? msg.id,
    }));

    return {
      messages,
      total:     response.data.pagination?.total     ?? messages.length,
      page:      response.data.pagination?.page      ?? page,
      page_size: response.data.pagination?.page_size ?? page_size,
      has_more:  response.data.pagination?.has_more  ?? false,
    };
  },

  sendMessage: async (phoneNumber: string, message: string): Promise<any> => {
    const encodedPhone = encodeURIComponent(phoneNumber);
    const response = await axiosInstance.post(`/inbox/conversations/${encodedPhone}/send`, { message });
    return response.data;
  },

  sendMedia: async (
    phoneNumber: string,
    file: File,
    caption?: string,
    isVoice?: boolean
  ): Promise<any> => {
    const encodedPhone = encodeURIComponent(phoneNumber);
    const form = new FormData();
    form.append('file', file);
    if (caption) {
      form.append('caption', caption);
    }
    if (isVoice) {
      form.append('is_voice', 'true');
    }
    // Do not set Content-Type manually — the browser must add the multipart boundary.
    const response = await axiosInstance.post(
      `/inbox/conversations/${encodedPhone}/media`,
      form,
      { timeout: 120000 }
    );
    return response.data;
  },

  markAsRead: async (phoneNumber: string): Promise<any> => {
    const encodedPhone = encodeURIComponent(phoneNumber);
    const response = await axiosInstance.post(`/inbox/conversations/${encodedPhone}/read`);
    return response.data;
  }
};

